import React, { useEffect, useState } from 'react';
import {
    Drawer,
    Form,
    Input,
    InputNumber,
    Select,
    Button,
    DatePicker,
    Switch,
    Tabs,
    Row,
    Col,
    Space,
    Divider,
    message,
    Card,
    Typography
} from 'antd';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import treeData from '../../Item/treeView.json';
import firebase from '../../../firebase';

const { TabPane } = Tabs;
const { TextArea } = Input;
const { Text } = Typography;

// Helper to extract file name from storage path/URL
const getFileNameFromPath = (path) => {
    if (!path) return '';
    const parts = path.split('/');
    return parts[parts.length - 1];
};

// Custom hook to resolve Firebase Storage path to HTTP Download URL
const useFirebaseImage = (src, storage) => {
    const [url, setUrl] = useState(null);
    useEffect(() => {
        if (src && storage) {
            try {
                if (src.startsWith('gs://') || src.startsWith('http://') || src.startsWith('https://')) {
                    storage.refFromURL(src)
                        .getDownloadURL()
                        .then(setUrl)
                        .catch(() => setUrl(null));
                } else {
                    storage.ref().child(src)
                        .getDownloadURL()
                        .then(setUrl)
                        .catch(() => setUrl(null));
                }
            } catch (e) {
                setUrl(null);
            }
        } else {
            setUrl(null);
        }
    }, [src, storage]);
    return url;
};

// Main Image / Sub Images Uploader & Preview Component
const ImageFieldPreview = ({ value, label, fieldName, storage, form }) => {
    const imageUrl = useFirebaseImage(value, storage);
    const fileName = getFileNameFromPath(value);

    const handleFileChange = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        // Path: items/currentTime_filename
        const path = `items/${Date.now()}_${file.name}`;
        const fileRef = storage.ref().child(path);

        try {
            await fileRef.put(file);
            const gsUrl = `gs://${fileRef.location.bucket}/${fileRef.location.path}`;
            form.setFieldsValue({ [fieldName]: gsUrl });
            message.success(`${label}をアップロードしました。`);
            // Trigger validation/re-render
            form.validateFields([fieldName]);
        } catch (err) {
            console.error(err);
            message.error(`${label}のアップロードに失敗しました。`);
        }
    };

    const handleClear = () => {
        form.setFieldsValue({ [fieldName]: null });
        // Force field validation to trigger re-render
        form.validateFields([fieldName]);
    };

    return (
        <Card size="small" style={{ marginBottom: 16, borderRadius: 8 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                <div style={{
                    width: 100,
                    height: 100,
                    border: '1px dashed #d9d9d9',
                    borderRadius: 8,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: '#fafafa',
                    overflow: 'hidden'
                }}>
                    {imageUrl ? (
                        <img src={imageUrl} alt={label} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    ) : (
                        <span style={{ color: '#999', fontSize: 12 }}>未登録</span>
                    )}
                </div>
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 8 }}>
                    <Text strong style={{ fontSize: 14 }}>{label}</Text>
                    {value ? (
                        <div>
                            <Text type="secondary" style={{ wordBreak: 'break-all', display: 'block', fontSize: 12, marginBottom: 6 }}>
                                ファイル名: {fileName}
                            </Text>
                            <Button size="small" danger icon={<DeleteOutlined />} onClick={handleClear}>
                                画像をクリア
                            </Button>
                        </div>
                    ) : (
                        <div>
                            <input
                                type="file"
                                accept="image/*"
                                onChange={handleFileChange}
                                style={{ display: 'none' }}
                                id={`file-upload-${fieldName}`}
                            />
                            <Button
                                size="small"
                                type="primary"
                                icon={<PlusOutlined />}
                                onClick={() => document.getElementById(`file-upload-${fieldName}`).click()}
                            >
                                画像を選択
                            </Button>
                        </div>
                    )}
                </div>
            </div>
            {/* Hidden field item for Ant Design Form tracking */}
            <Form.Item name={fieldName} noStyle>
                <Input type="hidden" />
            </Form.Item>
        </Card>
    );
};

// File Upload Component for SDS PDF
const FileFieldUpload = ({ value, label, fieldName, storage, form }) => {
    const fileName = getFileNameFromPath(value);

    const handleFileChange = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        const path = `sds/${Date.now()}_${file.name}`;
        const fileRef = storage.ref().child(path);

        try {
            await fileRef.put(file);
            const gsUrl = `gs://${fileRef.location.bucket}/${fileRef.location.path}`;
            form.setFieldsValue({ [fieldName]: gsUrl });
            message.success(`${label}をアップロードしました。`);
            form.validateFields([fieldName]);
        } catch (err) {
            console.error(err);
            message.error(`${label}のアップロードに失敗しました。`);
        }
    };

    const handleClear = () => {
        form.setFieldsValue({ [fieldName]: null });
        form.validateFields([fieldName]);
    };

    return (
        <Card size="small" style={{ marginBottom: 16, borderRadius: 8 }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <Text strong style={{ fontSize: 14 }}>{label}</Text>
                {value ? (
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        backgroundColor: '#fafafa',
                        padding: '8px 12px',
                        borderRadius: 6,
                        border: '1px solid #d9d9d9'
                    }}>
                        <Text ellipsis style={{ flex: 1, marginRight: 16 }}>{fileName}</Text>
                        <Button size="small" danger icon={<DeleteOutlined />} onClick={handleClear}>削除</Button>
                    </div>
                ) : (
                    <div>
                        <input
                            type="file"
                            accept=".pdf"
                            onChange={handleFileChange}
                            style={{ display: 'none' }}
                            id={`file-upload-${fieldName}`}
                        />
                        <Button
                            size="small"
                            type="primary"
                            icon={<PlusOutlined />}
                            onClick={() => document.getElementById(`file-upload-${fieldName}`).click()}
                        >
                            PDFファイルを選択
                        </Button>
                    </div>
                )}
            </div>
            <Form.Item name={fieldName} noStyle>
                <Input type="hidden" />
            </Form.Item>
        </Card>
    );
};

// Individual File Attachment Item Component
const FileAttachmentItem = ({ fileId, name, onRename, onDelete, storage }) => {
    const [downloadUrl, setDownloadUrl] = useState(null);
    const [fileName, setFileName] = useState(name);
    const [isEditingName, setIsEditingName] = useState(false);

    useEffect(() => {
        if (fileId && storage) {
            const db = firebase.firestore();
            db.collection('files').doc(fileId).get()
                .then(doc => {
                    if (doc.exists) {
                        const fileData = doc.data();
                        if (fileData && fileData.file) {
                            storage.ref(fileData.file).getDownloadURL()
                                .then(setDownloadUrl)
                                .catch(err => console.error('Error resolving download url:', err));
                        }
                    }
                })
                .catch(err => console.error('Error fetching file doc:', err));
        }
    }, [fileId, storage]);

    const handleSaveName = () => {
        setIsEditingName(false);
        if (fileName.trim() && fileName !== name) {
            onRename(fileName.trim());
        }
    };

    return (
        <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            backgroundColor: '#fafafa',
            padding: '8px 12px',
            borderRadius: 6,
            border: '1px solid #d9d9d9',
            marginBottom: 8
        }}>
            <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 12 }}>
                {isEditingName ? (
                    <Input
                        size="small"
                        value={fileName}
                        onChange={(e) => setFileName(e.target.value)}
                        onBlur={handleSaveName}
                        onPressEnter={handleSaveName}
                        style={{ width: '70%' }}
                        autoFocus
                    />
                ) : (
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                        <span
                            style={{ fontWeight: 500, cursor: 'pointer', textDecoration: 'underline' }}
                            onClick={() => setIsEditingName(true)}
                            title="ダブルクリックまたはクリックで名前を編集"
                        >
                            {name}
                        </span>
                        {downloadUrl && (
                            <a href={downloadUrl} target="_blank" rel="noopener noreferrer" style={{ fontSize: 11, color: '#1890ff' }}>
                                ダウンロード / 表示
                            </a>
                        )}
                    </div>
                )}
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
                {!isEditingName && (
                    <Button size="small" type="link" onClick={() => setIsEditingName(true)}>
                        名前変更
                    </Button>
                )}
                <Button size="small" type="link" danger onClick={onDelete}>
                    削除
                </Button>
            </div>
        </div>
    );
};

// Attachment Files (files) List and Uploader Component
const AttachmentsManager = ({ value = [], onChange, storage }) => {
    const handleFileChange = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        // 1. Upload file to storage
        const storagePath = `manual/${Date.now()}_${file.name}`;
        const fileRef = storage.ref().child(storagePath);

        try {
            await fileRef.put(file);

            // 2. Create document in Firestore 'files' collection
            const db = firebase.firestore();
            const lastDotIndex = file.name.lastIndexOf('.');
            const nameWithoutExtension = lastDotIndex !== -1 ? file.name.substring(0, lastDotIndex) : file.name;

            const docRef = await db.collection('files').add({
                file: fileRef.location.path,
                name: nameWithoutExtension
            });

            // 3. Append to files array
            const newFileItem = {
                file: docRef.id,
                name: nameWithoutExtension
            };
            const updatedFiles = [...value, newFileItem];
            onChange(updatedFiles);
            message.success('ファイルをアップロードして添付しました。');
        } catch (err) {
            console.error('File upload error:', err);
            message.error('ファイルのアップロードに失敗しました。');
        }
    };

    const handleRename = (index, newName) => {
        const updatedFiles = value.map((item, idx) => {
            if (idx === index) {
                return { ...item, name: newName };
            }
            return item;
        });
        onChange(updatedFiles);
    };

    const handleRemove = (index) => {
        const updatedFiles = value.filter((_, idx) => idx !== index);
        onChange(updatedFiles);
    };

    return (
        <Card title="添付ファイルリスト (取扱説明書、カタログ等)" size="small" style={{ borderRadius: 8 }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 12 }}>
                {value.length === 0 ? (
                    <Text type="secondary" style={{ padding: '8px 0', display: 'block' }}>
                        添付されたファイルはありません。
                    </Text>
                ) : (
                    value.map((item, index) => (
                        <FileAttachmentItem
                            key={index}
                            fileId={item.file}
                            name={item.name}
                            onRename={(newName) => handleRename(index, newName)}
                            onDelete={() => handleRemove(index)}
                            storage={storage}
                        />
                    ))
                )}
            </div>
            <div>
                <input
                    type="file"
                    onChange={handleFileChange}
                    style={{ display: 'none' }}
                    id="attachment-file-upload"
                />
                <Button
                    type="dashed"
                    block
                    icon={<PlusOutlined />}
                    onClick={() => document.getElementById('attachment-file-upload').click()}
                >
                    新規ファイルを追加 (アップロード)
                </Button>
            </div>
        </Card>
    );
};

const ProductFormDrawer = ({
    visible,
    onClose,
    initialValues,
    onSave,
    mode, // 'new', 'edit', 'duplicate'
    loading,
    storage
}) => {
    const [form] = Form.useForm();
    const [activeTab, setActiveTab] = useState('basic');

    // Category states
    const [majorCategories, setMajorCategories] = useState([]);
    const [middleCategories, setMiddleCategories] = useState([]);
    const [smallCategories, setSmallCategories] = useState([]);

    const [selectedMajor, setSelectedMajor] = useState(null);
    const [selectedMiddle, setSelectedMiddle] = useState(null);

    // Initial category load
    useEffect(() => {
        const majors = treeData.filter(item => item.level === 'major');
        setMajorCategories(majors);
    }, []);

    // Set initial values
    useEffect(() => {
        if (visible) {
            form.resetFields();
            setActiveTab('basic');
            if (initialValues && (mode === 'edit' || mode === 'duplicate')) {
                // If duplicate, append "(コピー)" to product name
                const productName = mode === 'duplicate'
                    ? `${initialValues['P4CLOUD商品名'] || ''} (コピー)`
                    : initialValues['P4CLOUD商品名'] || '';

                // Handle price array
                let priceVal = undefined;
                let expirationDateVal = null;
                if (initialValues.price && initialValues.price.length > 0) {
                    priceVal = initialValues.price[0].price;
                    if (initialValues.price[0].expiration_date) {
                        const dateObj = typeof initialValues.price[0].expiration_date.toDate === 'function'
                            ? initialValues.price[0].expiration_date.toDate()
                            : new Date(initialValues.price[0].expiration_date);
                        expirationDateVal = dayjs(dateObj);
                    }
                }

                // Handle transaction date (取引日)
                let transactionDateVal = null;
                if (initialValues['取引日']) {
                    const dateObj = typeof initialValues['取引日'].toDate === 'function'
                        ? initialValues['取引日'].toDate()
                        : new Date(initialValues['取引日']);
                    transactionDateVal = dayjs(dateObj);
                }

                // Setup categories
                const majorVal = initialValues['大分類'] || null;
                const middleVal = initialValues['中分類'] || null;
                const smallVal = initialValues['小分類'] || null;

                setSelectedMajor(majorVal);
                setSelectedMiddle(middleVal);

                if (majorVal) {
                    const majorItem = treeData.find(item => item.level === 'major' && item.code === majorVal);
                    if (majorItem) {
                        const middles = treeData.filter(item => item.level === 'middle' && item.categoryId === majorItem.ID);
                        setMiddleCategories(middles);

                        if (middleVal) {
                            const middleItem = treeData.find(item => item.level === 'middle' && item.code === middleVal && item.categoryId === majorItem.ID);
                            if (middleItem) {
                                const smalls = treeData.filter(item => item.level === 'small' && item.categoryId === middleItem.ID);
                                setSmallCategories(smalls);
                            } else {
                                setSmallCategories([]);
                            }
                        } else {
                            setSmallCategories([]);
                        }
                    } else {
                        setMiddleCategories([]);
                        setSmallCategories([]);
                    }
                } else {
                    setMiddleCategories([]);
                    setSmallCategories([]);
                }

                form.setFieldsValue({
                    ...initialValues,
                    'P4CLOUD商品名': productName,
                    priceAmount: priceVal,
                    expirationDate: expirationDateVal,
                    '取引日': transactionDateVal,
                    archive: initialValues.archive === true,
                    data: initialValues.data || [],
                    files: initialValues.files || []
                });
            } else {
                form.resetFields();
                setSelectedMajor(null);
                setSelectedMiddle(null);
                setMiddleCategories([]);
                setSmallCategories([]);
                form.setFieldsValue({
                    archive: false,
                    data: [],
                    files: []
                });
            }
        }
    }, [visible, initialValues, mode, form]);

    // Watch files/images form values for the custom renderers
    const watchImage = Form.useWatch('image', form);
    const watchImage2 = Form.useWatch('image2', form);
    const watchImage3 = Form.useWatch('image3', form);

    // Handle Major Category change
    const handleMajorChange = (value) => {
        setSelectedMajor(value);
        setSelectedMiddle(null);
        setSmallCategories([]);
        form.setFieldsValue({ '中分類': null, '小分類': null });

        if (value) {
            const majorItem = treeData.find(item => item.level === 'major' && item.code === value);
            if (majorItem) {
                const middles = treeData.filter(item => item.level === 'middle' && item.categoryId === majorItem.ID);
                setMiddleCategories(middles);
            } else {
                setMiddleCategories([]);
            }
        } else {
            setMiddleCategories([]);
        }
    };

    // Handle Middle Category change
    const handleMiddleChange = (value) => {
        setSelectedMiddle(value);
        form.setFieldsValue({ '小分類': null });

        if (value && selectedMajor) {
            const majorItem = treeData.find(item => item.level === 'major' && item.code === selectedMajor);
            const middleItem = treeData.find(item => item.level === 'middle' && item.code === value && item.categoryId === majorItem?.ID);
            if (middleItem) {
                const smalls = treeData.filter(item => item.level === 'small' && item.categoryId === middleItem.ID);
                setSmallCategories(smalls);
            } else {
                setSmallCategories([]);
            }
        } else {
            setSmallCategories([]);
        }
    };

    const handleSubmit = () => {
        form.validateFields()
            .then(values => {
                // Map values to Firestore payload
                const payload = { ...values };

                // Handle price array
                if (values.priceAmount !== undefined && values.priceAmount !== null) {
                    payload.price = [
                        {
                            price: Number(values.priceAmount),
                            expiration_date: values.expirationDate
                                ? values.expirationDate.toDate()
                                : new Date('2099-12-01')
                        }
                    ];
                } else {
                    payload.price = null;
                }
                delete payload.priceAmount;
                delete payload.expirationDate;

                // Handle transaction date (取引日)
                if (values['取引日']) {
                    payload['取引日'] = values['取引日'].toDate();
                } else {
                    payload['取引日'] = null;
                }

                // Handle archive status
                payload.archive = !!values.archive;

                // Automatically generate files_names list
                const filesList = values.files || [];
                payload.files = filesList;
                payload.files_names = filesList
                    .filter(f => f.file && f.name)
                    .map(f => f.name.trim());

                // Ensure No is a number if provided
                if (values['No'] !== undefined && values['No'] !== null) {
                    payload['No'] = Number(values['No']);
                }

                // Ensure numeric fields
                if (values['売単価'] !== undefined && values['売単価'] !== null) {
                    payload['売単価'] = Number(values['売単価']);
                }
                if (values['売価'] !== undefined && values['売価'] !== null) {
                    payload['売価'] = Number(values['売価']);
                }

                // Add audit fields
                payload.updatedAt = new Date();

                // Sanitize undefined values to null to prevent Firestore errors
                Object.keys(payload).forEach(key => {
                    if (payload[key] === undefined) {
                        payload[key] = null;
                    }
                });

                onSave(payload);
            })
            .catch(errorInfo => {
                console.error('Validation Failed:', errorInfo);
                message.error('入力内容にエラーがあります。各タブを確認してください。');
            });
    };

    return (
        <Drawer
            title={
                mode === 'edit'
                    ? '商品情報を編集'
                    : mode === 'duplicate'
                        ? '商品複製 (新しい商品として登録)'
                        : '新しい商品を登録'
            }
            width={850}
            onClose={onClose}
            open={visible}
            bodyStyle={{ paddingBottom: 80 }}
            extra={
                <Space>
                    <Button onClick={onClose}>キャンセル</Button>
                    <Button onClick={handleSubmit} type="primary" loading={loading}>
                        {mode === 'edit' ? '保存する' : '登録する'}
                    </Button>
                </Space>
            }
        >
            <Form
                form={form}
                layout="vertical"
                requiredMark={true}
                initialValues={{
                    archive: false,
                    data: [],
                    files: []
                }}
            >
                <Tabs activeKey={activeTab} onChange={setActiveTab} type="card">
                    <TabPane tab="基本情報" key="basic">
                        <Row gutter={16}>
                            <Col span={6}>
                                <Form.Item
                                    label="No"
                                    name="No"
                                    rules={[{ required: true, message: 'Noを入力してください' }]}
                                >
                                    <InputNumber style={{ width: '100%' }} min={1} placeholder="例: 1" />
                                </Form.Item>
                            </Col>
                            <Col span={18}>
                                <Form.Item
                                    label="P4CLOUD商品名"
                                    name="P4CLOUD商品名"
                                    rules={[{ required: true, message: '商品名を入力してください' }]}
                                >
                                    <Input placeholder="商品名を入力してください" />
                                </Form.Item>
                            </Col>
                        </Row>

                        <Row gutter={16}>
                            <Col span={24}>
                                <Form.Item
                                    label="P4CLOUD商品名ふりがな"
                                    name="P4CLOUD商品名ふりがな"
                                >
                                    <Input placeholder="ひらがなを入力してください" />
                                </Form.Item>
                            </Col>
                        </Row>

                        <Divider style={{ margin: '12px 0' }} />

                        <Row gutter={16}>
                            <Col span={8}>
                                <Form.Item
                                    label="大分類"
                                    name="大分類"
                                    rules={[{ required: true, message: '大分類を選択してください' }]}
                                >
                                    <Select
                                        placeholder="選択してください"
                                        onChange={handleMajorChange}
                                        allowClear
                                        showSearch
                                        optionFilterProp="label"
                                    >
                                        {majorCategories.map(item => (
                                            <Select.Option key={item.code} value={item.code} label={item.name2}>
                                                {item.name2}
                                            </Select.Option>
                                        ))}
                                    </Select>
                                </Form.Item>
                            </Col>
                            <Col span={8}>
                                <Form.Item
                                    label="中分類"
                                    name="中分類"
                                    rules={[{ required: true, message: '中分類を選択してください' }]}
                                >
                                    <Select
                                        placeholder="選択してください"
                                        onChange={handleMiddleChange}
                                        disabled={!selectedMajor}
                                        allowClear
                                        showSearch
                                        optionFilterProp="label"
                                    >
                                        {middleCategories.map(item => (
                                            <Select.Option key={item.code} value={item.code} label={item.name2}>
                                                {item.name2}
                                            </Select.Option>
                                        ))}
                                    </Select>
                                </Form.Item>
                            </Col>
                            <Col span={8}>
                                <Form.Item
                                    label="小分類"
                                    name="小分類"
                                    rules={[{ required: true, message: '小分類を選択してください' }]}
                                >
                                    <Select
                                        placeholder="選択してください"
                                        disabled={!selectedMiddle}
                                        allowClear
                                        showSearch
                                        optionFilterProp="label"
                                    >
                                        {smallCategories.map(item => (
                                            <Select.Option key={item.code} value={item.code} label={item.name2}>
                                                {item.name2}
                                            </Select.Option>
                                        ))}
                                    </Select>
                                </Form.Item>
                            </Col>
                        </Row>

                        <Divider style={{ margin: '12px 0' }} />

                        <Row gutter={16}>
                            <Col span={8}>
                                <Form.Item
                                    label="荷姿名称１"
                                    name="荷姿名称１"
                                    rules={[{ required: true, message: '荷姿名称１を入力してください' }]}
                                >
                                    <Input placeholder="例: 缶, 瓶, 箱" />
                                </Form.Item>
                            </Col>
                            <Col span={8}>
                                <Form.Item
                                    label="単位"
                                    name="単位"
                                    rules={[{ required: true, message: '単位を入力してください' }]}
                                >
                                    <Input placeholder="例: Kg, L, 個" />
                                </Form.Item>
                            </Col>
                            <Col span={8}>
                                <Form.Item
                                    label="容量・入数"
                                    name="容量・入数"
                                    rules={[{ required: true, message: '容量・入数を入力してください' }]}
                                >
                                    <Input placeholder="例: 16kg, 4L" />
                                </Form.Item>
                            </Col>
                        </Row>

                        <Row gutter={16}>
                            <Col span={8}>
                                <Form.Item
                                    label="発注単位"
                                    name="発注単位"
                                    rules={[{ required: true, message: '発注単位を入力してください' }]}
                                >
                                    <Input placeholder="例: 1缶単位" />
                                </Form.Item>
                            </Col>
                            <Col span={8}>
                                <Form.Item
                                    label="発注数"
                                    name="発注数"
                                    rules={[{ required: true, message: '発注数を入力してください' }]}
                                >
                                    <InputNumber style={{ width: '100%' }} min={0} placeholder="例: 1" />
                                </Form.Item>
                            </Col>
                            <Col span={8}>
                                <Form.Item
                                    label="発注数量"
                                    name="発注数量"
                                    rules={[{ required: true, message: '発注数量を入力してください' }]}
                                >
                                    <InputNumber style={{ width: '100%' }} min={0} placeholder="例: 16" />
                                </Form.Item>
                            </Col>
                        </Row>

                        <Row gutter={16}>
                            <Col span={8}>
                                <Form.Item label="色" name="色">
                                    <Input placeholder="例: 赤, 青, 白" />
                                </Form.Item>
                            </Col>
                            <Col span={8}>
                                <Form.Item label="容量" name="容量">
                                    <Input placeholder="例: 15Kg, 4L" />
                                </Form.Item>
                            </Col>
                            <Col span={8}>
                                <Form.Item label="分類" name="分類">
                                    <Input placeholder="例: 塗料, 溶剤" />
                                </Form.Item>
                            </Col>
                        </Row>

                        <Row gutter={16}>
                            <Col span={8}>
                                <Form.Item label="劇毒物区分名" name="劇毒物区分名">
                                    <Input placeholder="例: 劇物, 毒物, 該当外" />
                                </Form.Item>
                            </Col>
                            <Col span={8}>
                                <Form.Item label="危険物区分名" name="危険物区分名">
                                    <Input placeholder="例: 第4類, 該当外" />
                                </Form.Item>
                            </Col>
                            <Col span={8}>
                                <Form.Item label="取引日" name="取引日">
                                    <DatePicker style={{ width: '100%' }} placeholder="取引日を選択" />
                                </Form.Item>
                            </Col>
                        </Row>

                        <Row gutter={16}>
                            <Col span={8}>
                                <Form.Item label="アーカイブ済" name="archive" valuePropName="checked">
                                    <Switch checkedChildren="はい" unCheckedChildren="いいえ" />
                                </Form.Item>
                            </Col>
                        </Row>
                    </TabPane>

                    <TabPane tab="価格情報" key="price">
                        <Card title="販売価格と有効期限の設定" size="small" style={{ marginBottom: 16, borderRadius: 8 }}>
                            <Row gutter={16}>
                                <Col span={12}>
                                    <Form.Item
                                        label="単価 (価格) (円)"
                                        name="priceAmount"
                                        rules={[
                                            { required: true, message: '単価を入力してください' },
                                            { type: 'number', message: '有効な数値を入力してください' }
                                        ]}
                                    >
                                        <InputNumber
                                            style={{ width: '100%' }}
                                            formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                                            parser={value => value.replace(/\$\s?|(,*)/g, '')}
                                            placeholder="単価を入力"
                                            min={0}
                                        />
                                    </Form.Item>
                                </Col>
                                <Col span={12}>
                                    <Form.Item
                                        label="価格有効期限"
                                        name="expirationDate"
                                        rules={[{ required: true, message: '価格有効期限を選択してください' }]}
                                    >
                                        <DatePicker style={{ width: '100%' }} placeholder="有効期限を選択 (デフォルト: 2099-12-01)" />
                                    </Form.Item>
                                </Col>
                            </Row>
                        </Card>

                        <Row gutter={16}>
                            <Col span={8}>
                                <Form.Item label="売単価 (円)" name="売単価">
                                    <InputNumber
                                        style={{ width: '100%' }}
                                        formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                                        parser={value => value.replace(/\$\s?|(,*)/g, '')}
                                        placeholder="売単価を入力"
                                        min={0}
                                    />
                                </Form.Item>
                            </Col>
                            <Col span={8}>
                                <Form.Item
                                    label="売価 (円)"
                                    name="売価"
                                    rules={[{ required: true, message: '売価を入力してください' }]}
                                >
                                    <InputNumber
                                        style={{ width: '100%' }}
                                        formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                                        parser={value => value.replace(/\$\s?|(,*)/g, '')}
                                        placeholder="売価を入力"
                                        min={0}
                                    />
                                </Form.Item>
                            </Col>
                            <Col span={8}>
                                <Form.Item
                                    label="消費税率"
                                    name="消費税率"
                                    rules={[{ required: true, message: '消費税率を選択してください' }]}
                                >
                                    <Select placeholder="消費税率を選択">
                                        <Select.Option value={0.1}>10% (通常税率)</Select.Option>
                                        <Select.Option value={0.08}>8% (軽減税率)</Select.Option>
                                        <Select.Option value={0}>0% (無税/非課税)</Select.Option>
                                    </Select>
                                </Form.Item>
                            </Col>
                        </Row>
                    </TabPane>

                    <TabPane tab="メーカー・仕入先情報" key="maker">
                        <Card title="メーカー情報" size="small" style={{ marginBottom: 16, borderRadius: 8 }}>
                            <Row gutter={16}>
                                <Col span={12}>
                                    <Form.Item
                                        label="正式メーカー名"
                                        name="正式メーカー名"
                                        rules={[{ required: true, message: '正式メーカー名を入力してください' }]}
                                    >
                                        <Input placeholder="例: アサヒペン, 日本ペイント" />
                                    </Form.Item>
                                </Col>
                                <Col span={12}>
                                    <Form.Item label="メーカー商品コード" name="メーカー商品コード">
                                        <Input placeholder="メーカーの商品型番/コード" />
                                    </Form.Item>
                                </Col>
                            </Row>
                            <Row gutter={16}>
                                <Col span={12}>
                                    <Form.Item
                                        label="正式メーカー商品名"
                                        name="正式メーカー商品名"
                                        rules={[{ required: true, message: '正式メーカー商品名を入力してください' }]}
                                    >
                                        <Input placeholder="正式なメーカー商品名" />
                                    </Form.Item>
                                </Col>
                                <Col span={12}>
                                    <Form.Item label="メーカー法人番号" name="メーカー法人番号">
                                        <Input placeholder="13桁の法人番号" maxLength={13} />
                                    </Form.Item>
                                </Col>
                                {/* <Col span={12}>
                                    <Form.Item label="正式メーカー商品名かな" name="正式メーカー商品名かな">
                                        <Input placeholder="正式名ふりがな" />
                                    </Form.Item>
                                </Col> */}
                            </Row>
                        </Card>

                        <Card title="仕入先情報" size="small" style={{ borderRadius: 8 }}>
                            <Row gutter={16}>
                                <Col span={12}>
                                    <Form.Item
                                        label="仕入先（本社）"
                                        name="仕入先（本社）"
                                        rules={[{ required: true, message: '仕入先（本社）を入力してください' }]}
                                    >
                                        <Input placeholder="仕入先の会社名/本社名" />
                                    </Form.Item>
                                </Col>
                                <Col span={12}>
                                    <Form.Item
                                        label="仕入先事業所名"
                                        name="仕入先事業所名"
                                        rules={[{ required: true, message: '仕入先事業所名を入力してください' }]}
                                    >
                                        <Input placeholder="仕入先の事業所名" />
                                    </Form.Item>
                                </Col>
                            </Row>
                            <Row gutter={16}>
                                <Col span={12}>
                                    <Form.Item label="仕入先事業所コード" name="仕入先事業所コード">
                                        <Input placeholder="仕入先事業所の管理コード" />
                                    </Form.Item>
                                </Col>
                                <Col span={12}>
                                    <Form.Item label="仕入先法人番号" name="仕入先法人番号">
                                        <Input placeholder="13桁の仕入先法人番号" maxLength={13} />
                                    </Form.Item>
                                </Col>
                            </Row>
                            <Row gutter={16}>
                                <Col span={12}>
                                    <Form.Item label="仕入先EDIコード商品コード" name="仕入先EDIコード商品コード">
                                        <Input placeholder="仕入先EDIコード" />
                                    </Form.Item>
                                </Col>
                                <Col span={12}>
                                    <Form.Item label="仕入先発注コード" name="仕入先発注コード">
                                        <Input placeholder="仕入先発注コード" />
                                    </Form.Item>
                                </Col>
                            </Row>
                            <Row gutter={16}>
                                <Col span={24}>
                                    <Form.Item label="販売企業商品コード" name="販売企業商品コード">
                                        <Input placeholder="販売会社の商品コード" />
                                    </Form.Item>
                                </Col>
                            </Row>
                        </Card>
                    </TabPane>

                    <TabPane tab="詳細仕様" key="detailSpecs">
                        <Card title="追加仕様情報" size="small">
                            <Form.List name="data">
                                {(fields, { add, remove }) => (
                                    <>
                                        {fields.map(({ key, name, ...restField }) => (
                                            <Row key={key} gutter={16} align="middle" style={{ marginBottom: 12 }}>
                                                <Col span={10}>
                                                    <Form.Item
                                                        {...restField}
                                                        name={[name, 'title']}
                                                        rules={[{ required: true, message: 'タイトルを入力してください' }]}
                                                        style={{ marginBottom: 0 }}
                                                    >
                                                        <Input placeholder="項目名 (例: 仕様, 商品説明)" />
                                                    </Form.Item>
                                                </Col>
                                                <Col span={12}>
                                                    <Form.Item
                                                        {...restField}
                                                        name={[name, 'value']}
                                                        rules={[{ required: true, message: '値を入力してください' }]}
                                                        style={{ marginBottom: 0 }}
                                                    >
                                                        <TextArea autoSize={{ minRows: 1, maxRows: 6 }} placeholder="内容を入力してください" />
                                                    </Form.Item>
                                                </Col>
                                                <Col span={2}>
                                                    <Button
                                                        type="text"
                                                        danger
                                                        icon={<DeleteOutlined />}
                                                        onClick={() => remove(name)}
                                                    />
                                                </Col>
                                            </Row>
                                        ))}
                                        <Form.Item style={{ marginBottom: 0 }}>
                                            <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>
                                                仕様項目を追加
                                            </Button>
                                        </Form.Item>
                                    </>
                                )}
                            </Form.List>
                        </Card>
                    </TabPane>

                    <TabPane tab="ファイル & 画像" key="files">
                        <ImageFieldPreview
                            value={watchImage}
                            label="メイン画像 (image)"
                            fieldName="image"
                            storage={storage}
                            form={form}
                        />
                        <ImageFieldPreview
                            value={watchImage2}
                            label="サブ画像2 (image2)"
                            fieldName="image2"
                            storage={storage}
                            form={form}
                        />
                        <Divider style={{ margin: '20px 0' }} />
                        <Form.Item name="files" noStyle>
                            <AttachmentsManager storage={storage} />
                        </Form.Item>
                    </TabPane>
                </Tabs>
            </Form>
        </Drawer>
    );
};

export default ProductFormDrawer;
